import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalLeadership } from "@common/ADAPT.Common.Model/organisation/cultural-leadership";
import { OutstandingSurveyResponse } from "@common/ADAPT.Common.Model/organisation/outstanding-survey-response";
import { Survey, SurveyStatus } from "@common/ADAPT.Common.Model/organisation/survey";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { IAdaptLinkObject } from "@common/route/route.service";
import { UserService } from "@common/user/user.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { DeleteActivityDialogComponent } from "@org-common/lib/activities/delete-activity-dialog/delete-activity-dialog.component";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { CulturalActivityMetadataByType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { CulturalLeadershipQueryUtilities } from "@org-common/lib/cultural-leadership/cultural-leadership-query-utilities";
import { PeerCatchupAuthService } from "@org-common/lib/peer-catchup/peer-catchup-auth.service";
import { PeerCatchupUiService } from "@org-common/lib/peer-catchup/peer-catchup-ui.service";
import { SurveyService } from "@org-common/lib/survey/survey.service";
import { SurveyAuthService } from "@org-common/lib/survey/survey-auth.service";
import { SurveyPageRoute } from "@org-common/lib/survey/survey-page/survey-page.component";
import { SurveyUiService } from "@org-common/lib/survey/survey-ui.service";
import moment from "moment";
import { defer, EMPTY, firstValueFrom, lastValueFrom } from "rxjs";
import { filter, switchMap, tap } from "rxjs/operators";

@Component({
    selector: "adapt-activity-card",
    templateUrl: "./activity-card.component.html",
    styleUrl: "./activity-card.component.scss",
    standalone: false,
})
export class ActivityCardComponent extends BaseComponent implements OnInit {
    public readonly CulturalActivityType = CulturalActivityType;
    public readonly SurveyStatus = SurveyStatus;

    @Input({ required: true }) public activity!: CulturalActivity;
    @Input() public highlightOverdue = true;

    @Input() public allowActions = true;

    @Output() public activityComplete = new EventEmitter<void>();

    public survey?: Survey;
    public outstandingSurveyResponse?: OutstandingSurveyResponse;
    public canEditSurvey = false;
    public canEditPeerCatchups = false;
    public culturalLeadershipConfiguration?: CulturalLeadership;
    public isCulturalLeader = false;

    public menuItems?: IAdaptMenuItem[];
    public routeLink?: IAdaptLinkObject;

    public constructor(
        private commonDataService: CommonDataService,
        private userService: UserService,
        private culturalActivityService: CulturalActivityService,
        private authorisationService: AuthorisationService,
        private culturalLeadershipFrameworkAuthService: CulturalLeadershipFrameworkAuthService,
        private surveyService: SurveyService,
        private surveyUiService: SurveyUiService,
        private surveyAuthService: SurveyAuthService,
        private peerCatchupUiService: PeerCatchupUiService,
        private dialogService: AdaptCommonDialogService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        rxjsBreezeService.entityTypeChanged(CulturalActivity).pipe(
            filter((cce) => cce.culturalActivityId === this.activity.culturalActivityId),
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();

        rxjsBreezeService.entityTypeChanged(OutstandingSurveyResponse).pipe(
            filter((osr) => this.outstandingSurveyResponse?.outstandingSurveyResponseId === osr.outstandingSurveyResponseId),
            this.takeUntilDestroyed(),
        ).subscribe((osr) => {
            // users osr will become detached when deleted on server side after response submitted
            if (osr.entityAspect.entityState.isDetached()) {
                this.outstandingSurveyResponse = undefined;
            }
        });

        rxjsBreezeService.entityTypeChanged(Survey).pipe(
            filter((survey) => this.survey?.surveyId === survey.surveyId),
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public get culturalActivityTypeMetadata() {
        return CulturalActivityMetadataByType[this.activity.type];
    }

    public get otherPersonId() {
        const currentPersonId = this.userService.currentPerson?.personId;

        if (this.activity.person1Id === currentPersonId) {
            return this.activity.person2Id;
        } else if (this.activity.person2Id === currentPersonId) {
            return this.activity.person1Id;
        }

        return this.activity.person1Id;
    }

    public get currentPersonIsCulturalLeaderForActivity() {
        // cultural leader is always person2
        const currentPersonId = this.userService.currentPerson?.personId;
        return !!this.activity.person2Id && this.activity.person2Id === currentPersonId;
    }

    public get currentPersonWithoutCulturalLeader() {
        const currentPersonId = this.userService.currentPerson?.personId;
        return !this.activity.person2Id && this.activity.person1Id === currentPersonId;
    }

    public get dueDate() {
        if (this.activity.extensions.isSurvey && this.survey) {
            return this.isStartedSurvey
                ? this.survey.endTime
                : this.survey.startTime;
        }

        return this.activity.date;
    }

    public get isSurvey() {
        return this.activity.extensions.isSurvey && !!this.survey;
    }

    public get isUnscheduledSurvey() {
        return this.activity.extensions.isSurvey && (!this.activity.entityId || !this.survey);
    }

    public get dueText() {
        if (this.activity.isCompleted) {
            return "Completed";
        }

        if (this.isUnscheduledSurvey) {
            return "Suggested date";
        }

        if (this.isSurvey) {
            return this.isStartedSurvey ? "Closes" : "Starts";
        }

        return "Due";
    }

    public get isOverdue() {
        return moment().startOf("day").isSameOrAfter(moment(this.dueDate).startOf("day"));
    }

    public get hasRescheduled() {
        return this.activity.extensions.hasRescheduled
            // cannot tell if survey has changed since we only track the start date.
            // only end date will change for running surveys.
            // if they've added a comment, that likely means the date changed
            || (this.isSurvey && this.activity.supplementaryData?.comment);
    }

    public get isStartedSurvey() {
        return this.isSurvey && this.survey?.status === SurveyStatus.Started;
    }

    public get canStartActivity() {
        if (this.isUnscheduledSurvey) {
            return this.canEditSurvey;
        }

        if (this.isSurvey) {
            return this.isStartedSurvey && !!this.outstandingSurveyResponse;
        }

        if (this.activity.type === CulturalActivityType.PeerCatchUp) {
            return this.canEditPeerCatchups && !this.activity.isCompleted;
        }

        return !this.activity.isCompleted;
    }

    public get startActivityIconClass() {
        if (this.isUnscheduledSurvey) {
            return "fal fa-calendar-lines-pen";
        }

        return "fal fa-play-circle";
    }

    public get startActivityText() {
        if (this.isUnscheduledSurvey) {
            return "Schedule survey";
        }

        const meta = this.culturalActivityTypeMetadata;
        return meta.actionText ?? "Start " + meta.shortTitle.toLocaleLowerCase();
    }

    public get canReschedule() {
        if (this.isUnscheduledSurvey) {
            return false;
        }

        if (this.isSurvey) {
            return this.canEditSurvey;
        }

        return !this.activity.isCompleted;
    }

    public async ngOnInit() {
        await this.updateData();
    }

    private async updateData() {
        if (this.allowActions) {
            this.allowActions = !this.activity.isCompleted;
        }

        this.isCulturalLeader = await this.authorisationService.promiseToGetHasAccess(CulturalLeadershipFrameworkAuthService.ConfigureCohorts);

        if (!this.isAlto && this.culturalLeadershipFrameworkAuthService.currentPersonHasPermissionToReadCohorts()) {
            this.culturalLeadershipConfiguration = await new CulturalLeadershipQueryUtilities(this.commonDataService).promiseToGetCulturalLeadershipConfiguration();
        }

        if (this.activity.extensions.isSurvey) {
            if (this.activity.entityId) {
                this.survey = await lastValueFrom(this.culturalActivityService.getEntityForCulturalActivity(this.activity)) as Survey | undefined;
                if (this.survey) {
                    if (this.survey.status === SurveyStatus.Started) {
                        // we should have queried the outstanding responses (for at least the current person) already when building the dashboard,
                        // so use that if present
                        const responses = this.survey.outstandingSurveyResponses.length > 0
                            ? this.survey.outstandingSurveyResponses
                            : await lastValueFrom(this.surveyService.getOutstandingSurveyResponsesForSurvey(this.survey.surveyId, false));
                        const currentPersonId = this.userService.currentPerson?.personId;
                        this.outstandingSurveyResponse = responses.find((osr) => osr.connection?.personId === currentPersonId);
                    }
                }
            }

            const surveyType = this.activity.extensions.surveyType;
            if (surveyType) {
                this.canEditSurvey = await firstValueFrom(this.surveyAuthService.forSurveyType(surveyType).hasEditAccessToSurveys$(this.activity.team));
            }
        }

        if (this.activity.type === CulturalActivityType.PeerCatchUp) {
            this.canEditPeerCatchups = await this.authorisationService.promiseToGetHasAccess(PeerCatchupAuthService.EditMyPeerCatchup);
        }

        if (this.hasRescheduled || this.isStartedSurvey) {
            // fetch the supp data if the event has been rescheduled
            // we need this for the info tooltip (to show the comment)
            // for surveys, we cannot tell if it's been rescheduled since we only track the start time. only a problem for started surveys.
            await lastValueFrom(this.culturalActivityService.getCulturalActivitySupplementaryData(this.activity));
        }

        // only allow deletion of survey activities for now
        // also PCU since those are manually scheduled
        if ((this.activity.extensions.isSurvey && this.canEditSurvey) || this.activity.type === CulturalActivityType.PeerCatchUp) {
            this.menuItems = [{
                ...MenuComponent.SmallRootMenu,
                padding: false,
                items: [{
                    text: this.survey ? "Delete survey" : "Delete activity",
                    icon: "fal fa-fw fa-trash-alt",
                    onClick: () => this.survey
                        ? this.survey.status === SurveyStatus.NotStarted
                            ? this.surveyUiService.promptToDeleteUpcomingSurvey(this.survey).subscribe()
                            : this.surveyUiService.promptToDeleteSurvey(this.survey).subscribe()
                        : this.dialogService.open(DeleteActivityDialogComponent, this.activity).subscribe(),
                }],
            }];
        }

        if (this.culturalActivityTypeMetadata.route$) {
            this.routeLink = await firstValueFrom(this.culturalActivityTypeMetadata.route$(this.activity, this.isCulturalLeader));
        }
    }

    @Autobind
    public rescheduleActivity() {
        const reschedule$ = this.culturalActivityService.rescheduleActivity(this.activity, true);

        if (this.activity.extensions.isSurvey && this.survey) {
            return this.surveyUiService.editSurvey(this.survey).pipe(
                switchMap(() => reschedule$),
            );
        }

        return reschedule$;
    }

    @Autobind
    public startActivity() {
        return defer(() => {
            const meta = this.culturalActivityTypeMetadata;
            const surveyType = this.activity.extensions.surveyType;
            if (this.activity.extensions.isSurvey && surveyType) {
                if (this.isUnscheduledSurvey) {
                    // don't allow dates before today
                    const date = moment.max([moment(this.activity.date), moment()]).toDate();
                    return this.surveyUiService.createSurvey(surveyType, this.activity.team, date);
                } else if (this.isStartedSurvey && this.outstandingSurveyResponse) {
                    return SurveyPageRoute.gotoRoute({}, {
                        token: this.outstandingSurveyResponse.token,
                        name: this.outstandingSurveyResponse.survey.name,
                    });
                }
            } else if (meta.type === CulturalActivityType.PeerCatchUp && this.activity.person1Id && this.activity.person2Id) {
                return this.peerCatchupUiService.recordNewCatchupFromActivity(this.activity);
            } else if (meta.action$) {
                return meta.action$(this.activity);
            }

            this.log.warn("startActivity: No action found for activity");
            return EMPTY;
        }).pipe(
            tap(() => this.activityComplete.emit()),
        );
    }
}
